import { useState, type CSSProperties } from "react";

/**
 * A person's face next to their name: the profile photo (migration 0029) when there is one, else
 * the initials. One component for every list on Desk, so a photo shows everywhere the moment it is
 * saved.
 *
 * The photo URL is signed and expires after some hours. A page left open past that gets a 404 for
 * the picture; the circle then falls back to the initials until the next fetch brings a fresh URL.
 *
 * The circle is decorative (`alt=""`): the name is always written next to it.
 */

export function initialsOf(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0] ?? "")
      .join("")
      .toUpperCase() || "?"
  );
}

const TONES = [
  "bg-accent-surface text-accent-strong",
  "bg-late-surface text-late-ink",
  "bg-quiet-surface text-quiet",
];

/** The same name always gets the same colour. */
export function avatarTone(name: string): string {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return TONES[hash % TONES.length]!;
}

export function Avatar({
  name,
  photoUrl = null,
  size = 36,
  tone,
  colors,
  className = "",
}: {
  name: string;
  photoUrl?: string | null;
  size?: number;
  /** Tailwind colour classes for the initials. The default picks one from the name. */
  tone?: string;
  /** Exact colours for the initials, for a circle in a group's colour. Wins over `tone`. */
  colors?: { background: string; color: string };
  className?: string;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const box: CSSProperties = { width: size, height: size };
  if (photoUrl && failedUrl !== photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        draggable={false}
        onError={() => setFailedUrl(photoUrl)}
        className={`shrink-0 rounded-full bg-sunken object-cover ${className}`}
        style={box}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className={`grid shrink-0 place-items-center rounded-full font-bold ${colors ? "" : (tone ?? avatarTone(name))} ${className}`}
      style={{ ...box, fontSize: Math.max(10, Math.round(size * 0.36)), ...(colors ?? {}) }}
    >
      {initialsOf(name)}
    </span>
  );
}

/**
 * A person's small face and name inline, for a name that stands alone in a line or a cell (a coach
 * in a list, in a header, on a card). Not for a name inside a sentence.
 */
export function AvatarName({
  name,
  photoUrl = null,
  size = 20,
  className = "",
}: {
  name: string;
  photoUrl?: string | null;
  size?: number;
  className?: string;
}) {
  return (
    <span className={`inline-flex max-w-full min-w-0 items-center gap-1.5 align-middle ${className}`}>
      <Avatar name={name} photoUrl={photoUrl} size={size} />
      <span dir="auto" className="min-w-0 truncate">{name}</span>
    </span>
  );
}
